import React, { useCallback, useEffect, useState } from "react";
import {
  Checkbox,
  Grid,
  FormControlLabel,
  makeStyles,
  Paper,
  Typography,
  InputAdornment,
  Link,
  Tooltip
} from "@material-ui/core";

import {
  getHoursMins,
  handleKeypress,
  remCalc,
  timeToSeconds
} from "../../../utils/helpers";
import Form from "../../common/Form";
import { LANDING_URL } from "../../data/constants";
import Button from "../../common/Button";
import API from "../../../utils/axios";
import Skeleton from "@material-ui/lab/Skeleton";

const useStyles = makeStyles(theme => ({
  cardContainer: {
    border: "1px solid #DDDDDD",
    padding: 24,
    marginBottom: 24,
    width: 696,
    maxWidth: 750,
    boxSizing: "border-box",
    "& .MuiIconButton-colorPrimary.Mui-checked svg": {
      color: theme.palette.primary.main
    }
  },
  cardTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: 16
  },
  cardText: {
    color: "#808080",
    fontSize: theme.typography.pxToRem(14)
  },
  switchLabel: {
    fontSize: theme.typography.pxToRem(15)
  },
  switchDescription: {
    color: "#808080",
    fontSize: theme.typography.pxToRem(14),
    marginLeft: theme.spacing(4)
  },
  betaFlag: {
    backgroundColor: "#D7F0E5",
    color: "#1A5B38",
    padding: theme.spacing(0.25, 0.5),
    lineHeight: ".75rem",
    fontSize: ".625rem",
    letterSpacing: "0.06em",
    display: "inline-block",
    position: "relative",
    fontWeight: "bold",
    top: -3.5,
    marginLeft: theme.spacing(1),
    borderRadius: theme.spacing(0.5)
  },
  btnSave: {
    marginTop: 8,
    textTransform: "none",
    boxShadow: "none !important",
    minWidth: 80,
    padding: theme.spacing(1.25, 2),
    lineHeight: "1.1rem",
    "&.Mui-disabled, &.Mui-disabled:hover": {
      backgroundColor: "#1329FE",
      color: "#fff",
      opacity: "38%"
    }
  }
}));

const maxLoad = 1000000;
const maxDuration = 1440;

const initialFormValues = {
  algorithm: "time",
  defaultDuration: 5,
  defaultUnits: 1,
  allowLateStops: false,
  allowLateStopsMinute: 1,
  allowBackhaul: false,
  backhaulLoadDurationPerStop: 1,
  fixedDepartureTime: false
};

const DefaultRouteOptimizationSettingsCard = props => {
  const { setSaveDisabled, routingSettings, loadingSettings } = props;

  // General
  const classes = useStyles();

  // States
  const [inputValues, setInputValues] = useState(initialFormValues);
  const [errorValues, setErrorValues] = useState({
    algorithm: "",
    defaultDuration: "",
    defaultUnits: "",
    allowLateStops: "",
    allowLateStopsMinute: "",
    allowBackhaul: "",
    backhaulLoadDurationPerStop: "",
    fixedDepartureTime: ""
  });

  const [submitting, setSubmitting] = useState(false);
  const [formChanged, setFormChanged] = useState(false);

  // Callbacks
  const updateInputValues = useCallback(
    (fieldName, fieldValue) => {
      setInputValues(previousInputValues => ({
        ...previousInputValues,
        [fieldName]: fieldValue
      }));
      if (fieldName === "allowBackhaul" && fieldValue === true) {
        updateInputValues("fixedDepartureTime", true);
      }
      setSaveDisabled(false);
      setFormChanged(true);
    },
    [setSaveDisabled]
  );

  const handleNumberInputChange = (event, maxValue) => {
    const { name, value } = event.target;
    updateInputValues(
      name,
      maxValue && value > Number(maxValue) ? maxValue : value
    );
  };

  const handleSubmit = () => {
    const errors = {
      defaultDuration: !inputValues.defaultDuration
        ? "Duration cannot be empty."
        : "",
      defaultUnits: !inputValues.defaultUnits
        ? "Unit count cannot be empty."
        : "",
      allowLateStopsMinute:
        inputValues.allowLateStops === true && !inputValues.allowLateStopsMinute
          ? "Cannot be empty."
          : "",
      backhaulLoadDurationPerStop:
        inputValues.allowBackhaul === true &&
        !inputValues.backhaulLoadDurationPerStop
          ? "Cannot be empty."
          : ""
    };
    setErrorValues(previousErrorValues => ({
      ...previousErrorValues,
      ...errors
    }));

    if (Object.values(errors).filter(Boolean).length === 0) {
      setSubmitting(true);
      const data = {
        algorithm: inputValues.algorithm,
        default_duration: inputValues.defaultDuration,
        default_load: inputValues.defaultUnits,
        allow_late_stops: inputValues.allowLateStops,
        allow_late_stops_minute: inputValues.allowLateStops
          ? inputValues.allowLateStopsMinute
          : null,
        allow_backhaul: inputValues.allowBackhaul,
        backhaul_load_duration_per_stop: inputValues.allowBackhaul
          ? inputValues.backhaulLoadDurationPerStop
          : null,
        fixed_departure_time: inputValues.fixedDepartureTime
      };

      API.put("/v1/settings/projects", data)
        .then(() => {
          setSaveDisabled(true);
          setFormChanged(false);
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => setSubmitting(false));
    }
  };

  // Memos
  const firstFormData = [
    [
      {
        disabled: submitting,
        componentType: "select",
        label: "Optimization preference",
        name: "algorithm",
        shrink: true,
        options: [
          ["time", "Fastest time"],
          ["distance", "Shortest distance"]
        ],
        onChange: event => {
          updateInputValues("algorithm", event.target.value);
        }
      }
    ],
    [
      {
        disabled: submitting,
        renderInputField: true,
        label: "Default duration (min.)",
        name: "defaultDuration",
        onChange: event => {
          handleNumberInputChange(event, maxDuration);
        },
        onKeyDown: event => handleKeypress(event)
      },
      {
        disabled: submitting,
        renderInputField: true,
        label: "Default unit count",
        name: "defaultUnits",
        onChange: event => {
          handleNumberInputChange(event, maxLoad);
        },
        onKeyDown: event => handleKeypress(event)
      }
    ]
  ];

  const secondFormData = [
    [
      {
        disabled: submitting,
        renderInputField: true,
        label: "Allow late stops",
        name: "allowLateStopsMinute",
        onChange: event => {
          handleNumberInputChange(event, maxDuration);
        },
        onKeyDown: event => handleKeypress(event),
        endAdornment: <InputAdornment position="end">Min.</InputAdornment>
      }
    ]
  ];

  const thirdFormData = [
    [
      {
        disabled: submitting,
        renderInputField: true,
        label: "Loading time (min.)",
        name: "backhaulLoadDurationPerStop",
        onChange: event => {
          handleNumberInputChange(event, maxDuration);
        },
        onKeyDown: event => handleKeypress(event)
      }
    ]
  ];

  // Effects
  useEffect(() => {
    if (routingSettings !== null) {
      setInputValues(routingSettings);
    }
  }, [routingSettings]);

  return (
    <Paper elevation={0} className={classes.cardContainer}>
      <Typography className={classes.cardTitle}>
        Default routing settings
      </Typography>
      <Typography className={classes.cardText}>
        Set the default routing settings. You can override this from within each
        project.
      </Typography>
      {loadingSettings && (
        <Grid container direction="column">
          <Grid item>
            <Skeleton height={70} width={382} />
          </Grid>
          <Grid item container>
            <Skeleton height={70} width={181} />
            <Skeleton height={70} width={181} style={{ marginLeft: 20 }} />
          </Grid>
          <Grid item>
            <Skeleton height={70} width={382} />
          </Grid>
          <Grid item>
            <Skeleton height={70} width={382} />
          </Grid>
          <Grid item>
            <Skeleton height={70} width={382} />
          </Grid>
          <Grid item>
            <Skeleton height={52} width={80} />
          </Grid>
        </Grid>
      )}
      {!loadingSettings && (
        <div style={{ maxWidth: 382, marginTop: 24 }}>
          <Form
            data={firstFormData}
            inputValues={inputValues}
            errorValues={errorValues}
          />
          <Grid
            container
            direction="column"
            spacing={2}
            style={{ marginBottom: 24 }}
          >
            <Grid item>
              <FormControlLabel
                color="primary"
                classes={{ label: classes.switchLabel }}
                control={
                  <Checkbox
                    color="primary"
                    onChange={event =>
                      updateInputValues("allowLateStops", event.target.checked)
                    }
                    checked={inputValues.allowLateStops}
                    disabled={submitting}
                  />
                }
                label="Allow late stops"
              />
              <Typography variant="body2" className={classes.switchDescription}>
                {"Max. number of minutes a driver can be late at a stop."}
              </Typography>
              {inputValues.allowLateStops && (
                <div style={{ marginTop: 24, marginLeft: 24 }}>
                  <Form
                    data={secondFormData}
                    inputValues={inputValues}
                    errorValues={errorValues}
                  />
                </div>
              )}
            </Grid>
            <Grid item>
              <FormControlLabel
                color="primary"
                classes={{ label: classes.switchLabel }}
                control={
                  <Checkbox
                    color="primary"
                    onChange={event =>
                      updateInputValues("allowBackhaul", event.target.checked)
                    }
                    checked={inputValues.allowBackhaul}
                    disabled={submitting}
                  />
                }
                label={
                  <>
                    Allow backhaul
                    <Tooltip
                      interactive
                      title={
                        <>
                          This feature is in beta testing, the way it works may
                          change at any time. User feedback is extremely
                          valuable to us, if you have any comments, please
                          contact us{" "}
                          <Link
                            href={`${LANDING_URL}/en/contact/support`}
                            target="_blank"
                          >
                            here
                          </Link>{" "}
                          with details.
                        </>
                      }
                      placement="bottom-start"
                      classes={{ tooltip: classes.tooltip }}
                      // enterNextDelay={1000}
                      enterDelay={1000}
                    >
                      <span className={classes.betaFlag}>BETA</span>
                    </Tooltip>
                  </>
                }
              />
              <Typography variant="body2" className={classes.switchDescription}>
                {"Vehicle can return to depot to load more stops."}
              </Typography>
              {inputValues.allowBackhaul && (
                <div style={{ marginTop: 24, marginLeft: 24 }}>
                  <Form
                    data={thirdFormData}
                    inputValues={inputValues}
                    errorValues={errorValues}
                  />
                </div>
              )}
            </Grid>
            <Grid item>
              <FormControlLabel
                color="primary"
                classes={{ label: classes.switchLabel }}
                control={
                  <Checkbox
                    color="primary"
                    onChange={event =>
                      updateInputValues(
                        "fixedDepartureTime",
                        event.target.checked
                      )
                    }
                    checked={inputValues.fixedDepartureTime}
                    disabled={submitting || inputValues.allowBackhaul}
                  />
                }
                label="Fixed departure time"
              />
              <Typography variant="body2" className={classes.switchDescription}>
                {"Vehicles always depart at the start of their shifts."}
              </Typography>
            </Grid>
          </Grid>
          <Button
            variant="contained"
            color="primary"
            disabled={!formChanged || submitting}
            className={classes.btnSave}
            onClick={handleSubmit}
            loading={submitting}
            progressSize={18}
          >
            Update
          </Button>
        </div>
      )}
    </Paper>
  );
};

export default DefaultRouteOptimizationSettingsCard;
